import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledContactSection = styled.section`
  max-width: 800px;
  margin: 150px auto;
  padding: 100px 50px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    margin: 100px 0;
    padding: 60px 30px;
  }

  @media (max-width: 480px) {
    margin: 60px 0;
    padding: 40px 20px;
  }

  .accent-line {
    font-size: var(--fz-sm);
    letter-spacing: 0.15em;
    color: var(--green);
    text-transform: uppercase;
    font-weight: 600;
    margin-bottom: 20px;
  }

  h2 {
    font-size: clamp(32px, 8vw, 48px);
    font-weight: 700;
    color: var(--lightest-slate);
    margin: 0 0 20px;
    line-height: 1.2;
  }

  .subtitle {
    max-width: 600px;
    font-size: var(--fz-lg);
    color: var(--slate);
    line-height: 1.8;
    margin-bottom: 50px;

    @media (max-width: 768px) {
      font-size: var(--fz-md);
      margin-bottom: 40px;
    }
  }

  .contact-links {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    justify-content: center;

    @media (max-width: 600px) {
      flex-direction: column;
      width: 100%;
    }
  }

  .contact-link {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 14px 32px;
    border: 2px solid var(--green);
    color: var(--green);
    text-decoration: none;
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    letter-spacing: 0.05em;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    font-weight: 600;

    &:hover {
      background-color: var(--green);
      color: var(--dark-navy);
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(100, 200, 100, 0.15);
    }

    &:active {
      transform: scale(0.98);
    }

    @media (max-width: 600px) {
      width: 100%;
      justify-content: center;
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const Contact = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  return (
    <StyledContactSection id="contact" ref={revealContainer}>
      <div className="accent-line">Get In Touch</div>

      <h2>Let's Connect & Collaborate</h2>

      <p className="subtitle">
        I'm always interested in hearing about new projects and opportunities. Whether you want to
        reach out, schedule a meeting, or just say hello, feel free to contact me.
      </p>

      <div className="contact-links">
        <a className="contact-link" href="mailto:contact@draatalents.org" title="Email us">
          📧 Email
        </a>
        <a
          className="contact-link"
          href="https://www.linkedin.com/groups/12720938"
          target="_blank"
          rel="noopener noreferrer"
          title="Connect on LinkedIn">
          💼 LinkedIn
        </a>
        <a
          className="contact-link"
          href="https://draatalents.org"
          target="_blank"
          rel="noopener noreferrer"
          title="Visit our website">
          🔗 Website
        </a>
      </div>
    </StyledContactSection>
  );
};

export default Contact;
